import { styled } from "styled-components";
import { OutlineOrangeButton, StyledButton } from "../components/Typography";
import { useCallback } from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { CheckIcon } from "@heroicons/react/24/outline";
import First from "./CartStepsContent/First";
import Second, { CheckoutFormData } from "./CartStepsContent/Second";
import Third from "./CartStepsContent/Third";
import Fourth from "./CartStepsContent/Fourth";
import { useCart } from "../contexts";

const StepperShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RailCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 18px 48px rgba(13, 18, 45, 0.08);
  border: 1px solid rgba(10, 17, 41, 0.06);

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 20px;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StepCard = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 20px;
  border: 1px solid
    ${({ theme, $isActive, $isCompleted }) =>
      $isActive || $isCompleted ? `${theme.colors.primary}55` : theme.colors.border};
  background:
    ${({ theme, $isActive, $isCompleted }) =>
      $isActive
        ? `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.primary}14 100%)`
        : $isCompleted
          ? `${theme.colors.primary}12`
          : theme.colors.white};
  min-height: 86px;

  @media (max-width: 767px) {
    min-height: 74px;
    padding: 12px;
  }
`;

const StepMarker = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 700;
  background:
    ${({ theme, $isActive, $isCompleted }) =>
      $isActive || $isCompleted ? theme.colors.primary : theme.colors.grey100};
  color:
    ${({ theme, $isActive, $isCompleted }) =>
      $isActive || $isCompleted ? theme.colors.blue500 : theme.colors.blue900};
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const StepEyebrow = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey};
`;

const StepTitle = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.35;
  font-weight: 700;
  color:
    ${({ theme, $isActive, $isCompleted }) =>
      $isActive || $isCompleted ? theme.colors.blue900 : theme.colors.blue500};
`;

const StepDescription = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey};
`;

const ContentCard = styled.div`
  padding: 30px 32px;
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 18px 48px rgba(13, 18, 45, 0.08);
  border: 1px solid rgba(10, 17, 41, 0.06);

  @media (max-width: 767px) {
    padding: 20px 18px;
    border-radius: 24px;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 767px) {
    width: 100%;
    flex-direction: column-reverse;
    margin-left: 0;
  }
`;

const ButtonWrapper = styled.div`
  @media (max-width: 767px) {
    width: 100%;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const DELIVERY_FIELDS: FieldPath<CheckoutFormData>[] = [
  "name",
  "surname",
  "email",
  "phone",
  "street",
  "aptNumber",
  "city",
  "zip",
];

const BILLING_FIELDS: FieldPath<CheckoutFormData>[] = [
  "billingName",
  "billingSurname",
  "billingStreet",
  "billingAptNumber",
  "billingCity",
  "billingZip",
];

const steps = [
  {
    title: "Položky",
    description: "Zkontrolujte košík",
    content: <First />,
  },
  {
    title: "Informace",
    description: "Doplňte údaje",
    content: <Second />,
  },
  {
    title: "Platba",
    description: "Dokončete objednávku",
    content: null,
  },
  {
    title: "Souhrn",
    description: "Máte hotovo",
    content: <Fourth />,
  },
];

const Stepper = () => {
  const {
    checkoutStep,
    setCheckoutStep,
    buyerInfo,
    setBuyerInfo,
    billingInfo,
    setBillingInfo,
    sameAddress,
    setSameAddress,
    setOrderId,
    setPaymentIntentClientSecret,
    startCheckout,
    completeCheckout,
  } = useCart();

  const methods = useForm<CheckoutFormData>({
    mode: "onBlur",
    shouldUnregister: true,
    defaultValues: {
      name: buyerInfo?.name || "",
      surname: buyerInfo?.surname || "",
      email: buyerInfo?.email || "",
      phone: buyerInfo?.phone || "",
      street: buyerInfo?.street || "",
      aptNumber: buyerInfo?.aptNumber || "",
      city: buyerInfo?.city || "",
      zip: buyerInfo?.zip || "",
      billingName: billingInfo?.name || "",
      billingSurname: billingInfo?.surname || "",
      billingStreet: billingInfo?.street || "",
      billingAptNumber: billingInfo?.aptNumber || "",
      billingCity: billingInfo?.city || "",
      billingZip: billingInfo?.zip || "",
      sameAddress,
    },
  });

  const next = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (checkoutStep === 1) {
      const values = methods.getValues();
      const fieldsToValidate = values.sameAddress
        ? DELIVERY_FIELDS
        : [...DELIVERY_FIELDS, ...BILLING_FIELDS];
      const isValid = await methods.trigger(fieldsToValidate);

      if (!isValid) {
        return;
      }

      setBuyerInfo({
        name: values.name,
        surname: values.surname,
        email: values.email,
        phone: values.phone,
        street: values.street,
        aptNumber: values.aptNumber,
        city: values.city,
        zip: values.zip,
      });
      setSameAddress(values.sameAddress);
      setBillingInfo(
        values.sameAddress
          ? null
          : {
              name: values.billingName,
              surname: values.billingSurname,
              street: values.billingStreet || "",
              aptNumber: values.billingAptNumber,
              city: values.billingCity || "",
              zip: values.billingZip || "",
            }
      );
      setOrderId(null);
      setPaymentIntentClientSecret(null);
      startCheckout();
    }

    setCheckoutStep(Math.min(checkoutStep + 1, 3));
  };

  const prev = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCheckoutStep(Math.max(checkoutStep - 1, 0));
  };

  const onPaymentComplete = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    completeCheckout();
  }, [completeCheckout]);

  const renderedSteps = steps.map((step, index) => ({
    ...step,
    content:
      index === 2 ? <Third onPaymentComplete={onPaymentComplete} /> : step.content,
  }));

  const isSummaryStep = checkoutStep === 3;
  const showPrev = checkoutStep > 0 && !isSummaryStep;
  const showNext = checkoutStep < 2;

  return (
    <FormProvider {...methods}>
      <StepperShell>
        <RailCard>
          <StepsGrid>
            {renderedSteps.map((step, index) => {
              const isActive = checkoutStep === index;
              const isCompleted = checkoutStep > index;

              return (
                <StepCard
                  key={step.title}
                  $isActive={isActive}
                  $isCompleted={isCompleted}
                >
                  <StepMarker $isActive={isActive} $isCompleted={isCompleted}>
                    {isCompleted ? (
                      <CheckIcon style={{ width: 18, height: 18 }} />
                    ) : (
                      index + 1
                    )}
                  </StepMarker>
                  <StepText>
                    <StepEyebrow>{`Krok ${index + 1}`}</StepEyebrow>
                    <StepTitle $isActive={isActive} $isCompleted={isCompleted}>
                      {step.title}
                    </StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepText>
                </StepCard>
              );
            })}
          </StepsGrid>
        </RailCard>

        <ContentCard>
          {renderedSteps[checkoutStep].content}

          {!isSummaryStep && (
            <ActionsRow>
              <div />
              <ActionGroup>
                {showPrev && (
                  <ButtonWrapper>
                    <OutlineOrangeButton onClick={prev}>
                      Předchozí
                    </OutlineOrangeButton>
                  </ButtonWrapper>
                )}
                {showNext && (
                  <ButtonWrapper>
                    <StyledButton onClick={next}>Pokračovat</StyledButton>
                  </ButtonWrapper>
                )}
              </ActionGroup>
            </ActionsRow>
          )}
        </ContentCard>
      </StepperShell>
    </FormProvider>
  );
};

export default Stepper;
